import { Group, Stack, useMantineTheme } from '@mantine/core';
import type { VersionHistoryOut } from '@repo/api-codegen';
import { Banner, Text, Title } from '@repo/foundations';
import type { ISecodaEntity } from '../../../../api';
import { RichEditor } from '../../../../components/RichEditor';
import { SnapshotProperties } from '../../../../components/SnapshotsModal/SnapshotProperties';
import type { IResourceContentProps } from '../../types';

interface ExtendedVersionHistory extends VersionHistoryOut {
	relevantChanges?: string[];
}

interface ActivityLogWithData {
	id: string;
	logged_at: string;
	old_data?: {
		verified?: boolean;
		pii?: boolean;
		archived?: boolean;
		title?: string;
		definition?: string;
		[key: string]: unknown;
	};
	new_data?: {
		verified?: boolean;
		pii?: boolean;
		archived?: boolean;
		title?: string;
		definition?: string;
		[key: string]: unknown;
	};
}

export function ResourceUpdatedContent({ selected }: IResourceContentProps) {
	const theme = useMantineTheme();
	const versionHistory = selected.computedMetadata.versionHistory as
		| ExtendedVersionHistory
		| undefined;

	if (!versionHistory) {
		return null;
	}

	const isLegacy = versionHistory.is_legacy;
	const snapshot = versionHistory.metadata.secoda_entity_property_snapshot;
	let { relevantChanges } = versionHistory;

	// Backwards compatibility: account for relevantChanges for description updates that still come as "definition_diff"
	if (
		selected.event === 'resource_description_updated' &&
		relevantChanges?.includes('definition_diff') &&
		!relevantChanges.includes('description_diff')
	) {
		relevantChanges = relevantChanges.map((change) =>
			change === 'definition_diff' ? 'description_diff' : change
		);
	}

	// Ensure relevantChanges is properly set for definition updates if versionHistory is stale
	if (
		selected.event === 'resource_definition_updated' &&
		(!relevantChanges || !relevantChanges.includes('definition_diff'))
	) {
		relevantChanges = ['definition_diff'];
	}

	// Calculate notification age to determine whether to use fresh or historical data
	const notificationDate = new Date(selected.created_at);
	const now = new Date();
	const notificationTime = notificationDate.getTime();
	const hoursSinceNotification =
		(now.getTime() - notificationTime) / (1000 * 60 * 60);

	// Find relevant title activity log for this notification
	const activityLogs = selected.computedMetadata.activityLogs as
		| ActivityLogWithData[]
		| undefined;
	const relevantTitleLog = activityLogs?.find((log) => {
		const logTime = new Date(log.logged_at).getTime();
		const timeDifference = Math.abs(logTime - notificationTime) / (1000 * 60);
		return (
			timeDifference <= 5 &&
			log.old_data?.title !== undefined &&
			log.new_data?.title !== undefined
		);
	});

	// Use activity log title data for historical accuracy, or fall back to version history
	let title: string | undefined;
	if (relevantTitleLog?.new_data?.title) {
		const { title: logTitle } = relevantTitleLog.new_data;
		title = logTitle;
	} else if (hoursSinceNotification < 1) {
		title =
			(selected.computedMetadata.secodaEntity as ISecodaEntity)?.title ??
			versionHistory.metadata.current.title ??
			snapshot?.title;
	} else {
		title = versionHistory.metadata.current.title ?? snapshot?.title;
	}
	const description =
		versionHistory.metadata.description_diff.markdown ??
		versionHistory.metadata.current.description ??
		snapshot?.description;

	const shouldHighlightDescription =
		relevantChanges?.includes('description_diff') ?? false;

	const shouldHighlightDocumentation =
		relevantChanges?.includes('definition_diff') ?? false;

	// Find the activity log that corresponds to this notification to show accurate historical data
	const relevantActivityLog = activityLogs?.find((log) => {
		const logTime = new Date(log.logged_at).getTime();
		// Find activity log within 5 minutes of notification creation
		const timeDifference = Math.abs(logTime - notificationTime) / (1000 * 60);
		return (
			timeDifference <= 5 &&
			log.old_data?.definition !== undefined &&
			log.new_data?.definition !== undefined
		);
	});

	// Use activity log data for historical accuracy, or fall back to version history
	let documentation: string;
	if (relevantActivityLog?.new_data?.definition) {
		const { definition } = relevantActivityLog.new_data;
		documentation = definition;
	} else if (hoursSinceNotification < 1) {
		documentation =
			(selected.computedMetadata.secodaEntity as ISecodaEntity)?.definition ??
			versionHistory.metadata.current.definition ??
			snapshot?.definition ??
			'';
	} else {
		documentation =
			versionHistory.metadata.current.definition ?? snapshot?.definition ?? '';
	}

	// Extract most recent owners/owner_groups/collections from activity logs (regardless of event timing)
	const getLatestArrayFromLogs = (
		key: 'owner_ids' | 'owner_group_ids' | 'collection_ids'
	): string[] | undefined => {
		const logs = (activityLogs ?? [])
			.slice()
			.sort(
				(a, b) =>
					new Date(b.logged_at).getTime() - new Date(a.logged_at).getTime()
			);
		for (const log of logs) {
			const raw = ((log.new_data as Record<string, unknown>) ?? {})[
				key
			] as unknown;
			if (Array.isArray(raw)) {
				return raw.filter((v): v is string => typeof v === 'string');
			}
		}
		return undefined;
	};

	const ownersFromLog = getLatestArrayFromLogs('owner_ids');
	const ownerGroupsFromLog = getLatestArrayFromLogs('owner_group_ids');
	const collectionsFromLog = getLatestArrayFromLogs('collection_ids');

	// Prepare currentEntity override to ensure SnapshotProperties shows accurate counts
	const baseCurrentEntity =
		(selected.computedMetadata.secodaEntity as ISecodaEntity | undefined) ??
		undefined;
	const currentEntityOverride: Partial<ISecodaEntity> = {};
	// Prefer most up-to-date values from current entity; fallback to activity logs if missing
	const computedOwners =
		baseCurrentEntity?.owners !== undefined
			? baseCurrentEntity.owners
			: ownersFromLog;
	const computedOwnerGroups =
		baseCurrentEntity?.owners_groups !== undefined
			? baseCurrentEntity.owners_groups
			: ownerGroupsFromLog;
	const computedCollections =
		baseCurrentEntity?.collections !== undefined
			? baseCurrentEntity.collections
			: collectionsFromLog;
	if (computedOwners) {
		currentEntityOverride.owners = computedOwners;
	}
	if (computedOwnerGroups) {
		currentEntityOverride.owners_groups = computedOwnerGroups;
	}
	if (computedCollections) {
		currentEntityOverride.collections = computedCollections;
	}
	const currentEntityForProperties =
		baseCurrentEntity || Object.keys(currentEntityOverride).length > 0
			? ({
					...(baseCurrentEntity as ISecodaEntity),
					...currentEntityOverride,
				} as ISecodaEntity)
			: undefined;

	return (
		<Stack spacing="2xl">
			{(title || description) && (
				<Group pt="xl" pb="xs" spacing="xs">
					<Stack spacing="md">
						<Title size="xl">{title || 'Untitled'}</Title>
						{description && (
							<div
								style={{
									backgroundColor: shouldHighlightDescription
										? theme.other.getColor('surface/emphasis/default')
										: 'transparent',
									borderRadius: shouldHighlightDescription
										? theme.radius.sm
										: 0,
									padding: shouldHighlightDescription ? theme.spacing.xs : 0,
									width: 'fit-content',
								}}
							>
								<RichEditor readOnly value={description} />
							</div>
						)}
					</Stack>
				</Group>
			)}

			<SnapshotProperties
				versionHistory={versionHistory}
				relevantChanges={relevantChanges}
				currentEntity={(() => {
					// Always pass entity when recent or when any properties changed that we render.
					const hasCustomPropertyChange =
						relevantChanges?.some((k) => k.startsWith('custom_properties.')) ??
						false;
					const shouldPass =
						hoursSinceNotification < 1 ||
						relevantChanges?.includes('tag_ids') ||
						relevantChanges?.includes('owner_ids') ||
						relevantChanges?.includes('owner_group_ids') ||
						relevantChanges?.includes('collection_ids') ||
						relevantChanges?.includes('team_ids') ||
						relevantChanges?.includes('verified') ||
						relevantChanges?.includes('pii') ||
						relevantChanges?.includes('published') ||
						hasCustomPropertyChange;
					return shouldPass ? currentEntityForProperties : undefined;
				})()}
			/>

			{isLegacy && (
				<Banner
					message="This is a legacy version history. We only have information about the document."
					tone="info"
					inCard
				/>
			)}

			<Stack spacing="sm">
				<Text size="sm" color="text/secondary/default">
					Documentation
				</Text>
				<div
					style={{
						backgroundColor: shouldHighlightDocumentation
							? theme.other.getColor('surface/emphasis/default')
							: 'transparent',
						borderRadius: shouldHighlightDocumentation ? theme.radius.sm : 0,
						padding: shouldHighlightDocumentation ? theme.spacing.xs : 0,
						width: 'fit-content',
					}}
				>
					<RichEditor
						readOnly
						value={documentation}
						limited={false}
						disableExtensions={[]}
					/>
				</div>
			</Stack>
		</Stack>
	);
}
